import React, { useState, useEffect, useRef } from "react";
import request from '../utils/request';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Typography,
  Layout,
  Popconfirm,
  Select,
  Transfer,
  Tag
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
// 导入batchSetStorePermissions API
import { getStores, createStore, updateStore, deleteStore, visibleUser, batchSetStorePermissions, checkStoreHealth, getSoftwareTypes } from "../api/store";
import { getMyUserList } from "../api/auth";
import { useAtom } from "jotai";
import { loggedInUserAtom } from "../store/userAtom";
import { Checkbox } from "antd";

const { Content } = Layout;
const StoreManagement = () => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [loggedInUser, setLoggedInUser] = useAtom(loggedInUserAtom);
  
  // 状态管理
  const [userList, setUserList] = useState([]); // 所有可选用户列表
  const [selectedUsers, setSelectedUsers] = useState([]); // 当前选中用户
  const [userLoading, setUserLoading] = useState(false); // 用户列表加载状态
  const [defaultSelectedUsers, setDefaultSelectedUsers] = useState([]);

    const [permissionVisible, setPermissionVisible] = useState(false); // 新增权限模态框状态
  const [currentPermissionStoreId, setCurrentPermissionStoreId] = useState(null);
  
  // 同步相关状态
  const [syncVisible, setSyncVisible] = useState(false);
  const [syncSource, setSyncSource] = useState(null);
  const [syncTargets, setSyncTargets] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);

  const handleSyncClick = (record) => {
    setSyncSource(record);
    setSyncTargets([]);
    setSyncVisible(true);
  };

  const handleSyncConfirm = async () => {
    if (syncTargets.length === 0) { messageApi.warning('请至少选择一个目标门店'); return; }
    setSyncLoading(true);
    try {
      await request.post('/sync/trigger', {
        source_address: syncSource.address,
        target_addresses: syncTargets,
      });
      messageApi.success('同步完成');
      setSyncVisible(false);
    } catch (e) {
      messageApi.error('同步失败: ' + (e.response?.data?.error || e.message));
    } finally {
      setSyncLoading(false);
    }
  };

  // 软件类型相关状态
  const [softwareTypes, setSoftwareTypes] = useState([]); // 软件类型列表
  const [softwareTypesLoading, setSoftwareTypesLoading] = useState(false); // 软件类型加载状态

  // 获取店铺列表
  const fetchStores = async () => {
    setLoading(true);
    try {
      const response = await getStores({
        user_id: loggedInUser?.user_id, // 传递user_id参数
        page: pagination.current,
        page_size: pagination.pageSize,
      });
      setDataSource(response.data.list);
      setTotal(response.data.total);
      getIPAddresses(response.data.list);
    } catch (error) {
      messageApi.error(
        "获取店铺列表失败: " + (error.response?.data?.msg || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const getIPAddresses = async (data = dataSource) => {
    // console.log(dataSource)
    // console.log('getIPAddresses调用时dataSource长度:', dataSource.length);
    // if (dataSource.length === 0) {
    //   console.log('dataSource为空，可能数据尚未加载完成');
    //   return [];
    // }
    const addresses = data.map(item => item.address);
    console.log('遍历输出IP地址:');
    // 调用健康检查接口
    // 遍历地址并进行健康检查，单个结果返回后立即更新
    addresses.forEach(async (ip, index) => {
      try {
        console.log(`${index + 1}. ${ip}`);
        const healthStatus = await checkStoreHealth(ip);
        console.log(`${ip} 健康状态:`, healthStatus);
        // 立即更新当前店铺状态
        setDataSource(prev => prev.map(store => 
          store.address == ip ? { ...store, status: 'success', healthData: healthStatus } : store
        ));
      } catch (error) {
        console.error(`${ip} 健康检查失败:`, error);
        // 立即更新当前店铺错误状态
        setDataSource(prev => prev.map(store => 
          store.address == ip ? { ...store, status: 'error', error: error.message } : store
        ));
      }
    });
    // 单个健康检查结果已实时更新到UI
    //return addresses;
  };

    const showPermissionModal = (record) => {
    setCurrentPermissionStoreId(record.store_id);
    setPermissionVisible(true);
    fetchUserData(record.store_id);
  };

  // 新增：关闭权限控制模态框
  const handlePermissionCancel = () => {
    setPermissionVisible(false);
    setSelectedUsers([]);
    setCurrentPermissionStoreId(null);
  };

    const handlePermissionSubmit = async () => {
    if (!currentPermissionStoreId) return;

    setSubmitLoading(true);
    try {
      // 构造权限数据
      const users = userList.map(user => ({
        user_id: user.value,
        status: selectedUsers.includes(user.value) ? 1 : 0
      }));

      await batchSetStorePermissions({
        store_id: currentPermissionStoreId,
        users: users
      });
      messageApi.success("用户权限更新成功");
      handlePermissionCancel();
    } catch (error) {
      messageApi.error(
        "权限更新失败: " + (error.response?.data?.msg || error.message)
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // 获取用户列表数据
  // 修改fetchUserData函数，设置selectedUsers初始值
  const fetchUserData = async (storeId) => {
    setUserLoading(true);
    try {
      const visibleUsersResponse = await visibleUser(storeId);
      const visibleUserIds = visibleUsersResponse.data.map(user => user.user_id);
      setDefaultSelectedUsers(visibleUserIds);
      setSelectedUsers(visibleUserIds);

      const allUsersResponse = await getMyUserList({
        page: 1,
        page_size: 999,
        master_id: loggedInUser?.user_id,
      });
      const usersWithValue = allUsersResponse.data.users.map(user => ({
        ...user,
        value: user.user_id,
        label: user.username,
      }));
      setUserList(usersWithValue);
      messageApi.success("权限信息加载完成")
    } catch (error) {
      messageApi.error(
        "获取用户列表失败: " + (error.response?.data?.msg || error.message)
      );
    } finally {
      setUserLoading(false);
    }
  };

  // 获取软件类型列表
  const fetchSoftwareTypes = async () => {
    setSoftwareTypesLoading(true);
    try {
      console.log('获取软件类型列表...');
      const response = await getSoftwareTypes();
      console.log('软件类型响应:', response);
      if (response.success && response.data) {
        console.log('设置软件类型:', response.data);
        setSoftwareTypes(response.data);
      } else {
        console.warn('响应格式不正确，使用默认值');
        setSoftwareTypes(['bohao2', 'bohao3', 'bohao3factura']);
      }
    } catch (error) {
      console.error("获取软件类型列表失败:", error.message);
      // 失败时使用默认值
      setSoftwareTypes(['bohao2', 'bohao3', 'bohao3factura']);
    } finally {
      setSoftwareTypesLoading(false);
    }
  };

  const intervalRef = useRef(null);
  // 每3秒检查一次健康状态
  useEffect(() => {
    if (dataSource.length>0) {
    // 立即执行一次
    // (async () => {
    //   console.log('初始健康状态检查:', new Date().toLocaleTimeString());
    //   await getIPAddresses(dataSource);
    // })();

    // 存储定时器ID到ref
    intervalRef.current = setInterval(async () => {
      console.log('定时检查健康状态:', new Date().toLocaleTimeString());
      await getIPAddresses(dataSource);
    }, 3000);

    // 清理定时器（使用ref存储的ID）
    return () => {
      console.log('清理定时器:', intervalRef.current);
      clearInterval(intervalRef.current);
    };
  }
  }, [dataSource]);

  // 组件挂载时获取数据
  useEffect(() => {
    fetchStores();
    // 获取软件类型列表（不依赖店铺数据）
    fetchSoftwareTypes();
  }, [pagination.current, pagination.pageSize]);

  // 监听模态框显示状态，当编辑模态框打开时获取用户数据
  useEffect(() => {
    if (visible && currentRecord) {
      fetchUserData(currentRecord.store_id);
    }
  }, [visible, currentRecord]);

  // 处理用户选择变化
  const handleUserSelectChange = (values) => {
    console.log(values, defaultSelectedUsers);
    console.log("list", userList);
    
    setSelectedUsers(values);
  };

  // 表单提交处理（新增/编辑）
  const handleSubmit = async () => {
  setSubmitLoading(true);
  try {
    const values = await form.validateFields();
    if (currentRecord) {
      await updateStore(currentRecord.store_id, { ...values, master_id: loggedInUser?.user_id });
      messageApi.success("店铺更新成功");
    } else {
      console.log(loggedInUser?.user_id)
      await createStore({ ...values, master_id: loggedInUser?.user_id });
      messageApi.success("店铺创建成功");
    }
    setVisible(false);
    form.resetFields();
    fetchStores();
  } catch (error) {
    messageApi.error(
      "操作失败: " + (error.response?.data?.msg || error.message)
    );
  } finally {
    setSubmitLoading(false);
  }
};

  // 关闭模态框时清空状态
  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setCurrentRecord(null);
    setSelectedUsers([]);
  };

  // 显示新增模态框
  const showAddModal = () => {
    setCurrentRecord(null);
    setSelectedUsers([]);
    form.resetFields();
    setVisible(true);
  };

  // 显示编辑模态框
  const showEditModal = (record) => {
    setCurrentRecord(record);
    form.setFieldsValue({
      name: record.store_name,
      address: record.address,
      location: record.location,
      phone: record.contact_number,
      type: record.type, // 软件类型字段，用于映射数据查询表
      api_key: record.api_key
    });
    setVisible(true);
  };

  // 删除店铺
  const handleDelete = async (id) => {
    setDeleteLoading(true);
    try {
      await deleteStore(id);
      messageApi.success("店铺删除成功");
      fetchStores();
    } catch (error) {
      messageApi.error(
        "删除失败: " + (error.response?.data?.msg || error.message)
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  // 分页变化处理
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // 表格列定义
  const columns = [
    {
      title: "ID",
      dataIndex: "store_id",
      key: "store_id",
      width: 80,
    },
    {
      title: "店铺名称",
      dataIndex: "store_name",
      key: "store_name",
    },
    // 软件类型
    {
      title: "软件类型",
      dataIndex: "type",
      key: "type",
      render: (type) => type || '-', // 为空时显示横线
    },
    {
      title: "IP地址",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "位置",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "电话",
      dataIndex: "contact_number",
      key: "contact_number",
    },
    {
      title: "API Key",
      dataIndex: "api_key",
      key: "api_key",
      render: (apiKey) => apiKey || '-',
    },
    {
      title: '店铺状态',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        if (status === 'success') return <Tag color="green">正常</Tag>;
        if (status === 'error') return <Tag color="red">异常</Tag>;
        return <Tag color="orange">未知</Tag>;
      },
    },
    {
      title: "创建于",
      dataIndex: "created_at",
      key: "created_at",
      render: (createdAt) => {
        if (!createdAt) return '';
        const date = new Date(createdAt);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}.${month}.${day} ${hours}:${minutes}`;
      }
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Button
            type="default"
            // icon={<SecurityOutlined />}
            size="small"
            onClick={() => showPermissionModal(record)}
          >
            权限控制
          </Button>
          <Button
            type="default"
            size="small"
            onClick={() => handleSyncClick(record)}
          >
            同步
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除此店铺吗？此操作不可撤销。"
            okText="确认"
            cancelText="取消"
            onConfirm={() => handleDelete(record.store_id)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={deleteLoading}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <Content style={{ padding: "0 50px" }}>
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddModal}
          >
            新增店铺
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={dataSource.map((item) => ({ ...item, key: item.id }))}
          loading={loading}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          onChange={handleTableChange}
          rowKey="id"
        />

        {/* 新增/编辑模态框 */}
        <Modal
          title={currentRecord ? "编辑店铺" : "新增店铺"}
          open={visible}
          onCancel={handleCancel} // 使用新的handleCancel方法
          onOk={handleSubmit}
          destroyOnClose
          confirmLoading={submitLoading}
          width={600}
        >
          <Form form={form} layout="vertical" name="store_form">
            <Form.Item
              name="name"
              label="店铺名称"
              rules={[{ required: true, message: "请输入店铺名称" }]}
            >
              <Input placeholder="请输入店铺名称" />
            </Form.Item>
            {/* 软件类型下拉框 - 不同类型对应不同的数据查询表 */}
            <Form.Item
              name="type"
              label="软件类型"
              rules={[{ required: true, message: "请选择软件类型" }]}
            >
              <Select 
                placeholder="请选择软件类型" 
                loading={softwareTypesLoading}
                notFoundContent={softwareTypesLoading ? "加载中..." : "暂无数据"}
              >
                {softwareTypes.map(type => (
                  <Select.Option key={type} value={type}>
                    {type}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="address"
              label="店铺IP地址"
              rules={[{ required: true, message: "请输入店铺IP地址" }]}
            >
              <Input placeholder="请输入店铺IP地址" />
            </Form.Item>
<Form.Item
              name="location"
              label="店铺位置"
              rules={[{ message: "请输入店铺位置" }]}
            >
              <Input placeholder="请输入店铺位置" />
            </Form.Item>
            <Form.Item
              name="phone"
              label="联系电话"
              rules={[{ message: "请输入联系电话" }]}
            >
              <Input placeholder="请输入联系电话" />
            </Form.Item>
            <Form.Item
              name="api_key"
              label="API Key"
              rules={[{ message: "请输入API Key" }]}
            >
              <Input placeholder="请输入API Key" />
            </Form.Item>
          </Form>
        </Modal>

                <Modal
          title="店铺权限控制"
          open={permissionVisible}
          onCancel={handlePermissionCancel}
          onOk={handlePermissionSubmit}
          confirmLoading={submitLoading}
          width={800}
        >
          <Transfer
            dataSource={userList}
            showSearch
            filterOption={(inputValue, item) =>
              item.label.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
            }
                  listStyle={{
        width: 250,
        height: 350,
      }}
            targetKeys={selectedUsers}
            onChange={handleUserSelectChange}
            render={item => item.label}
            loading={userLoading}
            titles={['可选用户', '已选用户']}
            rowKey={(record) => record.user_id}
          />
        </Modal>

        <Modal
          title={`同步商品：${syncSource?.store_name}`}
          open={syncVisible}
          onCancel={() => setSyncVisible(false)}
          onOk={handleSyncConfirm}
          confirmLoading={syncLoading}
        >
          <p>请选择目标门店：</p>
          <Checkbox.Group
            value={syncTargets}
            onChange={setSyncTargets}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {dataSource
              .filter(s => s.store_id !== syncSource?.store_id)
              .map(s => (
                <Checkbox key={s.store_id} value={s.address}>
                  {s.store_name}（{s.address}）
                </Checkbox>
              ))}
          </Checkbox.Group>
        </Modal>

      </Content>
    </div>
  );
};

export default StoreManagement;
